import i18n from '@/i18n';
import {
  editResultForAccount,
  type ForecastColumnData,
  type ForecastDetailColumnData,
  getDangerStyleWhenNegative,
  type SubForecastColumnData,
} from '@/utils/tables/forecast/forecast-column-data';
import { utils } from 'cesg-shared';

const { t } = i18n.global;

const BaseForecastColumns: ForecastColumnData[] = [
  {
    key: 'name',
    label: t('forecast.table.name'),
    align: 'has-text-left',
    sortable: true,
  },
  {
    key: 'budget',
    label: t('forecast.table.budget'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
  },
  {
    key: 'previousForecast',
    label: t('forecast.table.previousForecast'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
  },
  {
    key: 'forecast',
    label: t('forecast.table.forecast'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
  },
  {
    key: 'result',
    label: t('forecast.table.result'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
  },
  {
    key: 'remaining',
    label: t('forecast.table.remaining'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
    class: getDangerStyleWhenNegative,
  },
  {
    key: 'budgetLeft',
    label: t('forecast.table.budgetLeft'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
  },
  {
    key: 'earnings',
    label: t('forecast.table.earnings'),
    align: 'has-text-right',
    format: utils.formatNumber,
    sortable: true,
    class: getDangerStyleWhenNegative,
  },
  {
    key: 'accountTranslation',
    label: t('forecast.table.accountTranslation'),
    align: 'has-text-left',
    editable: () => true,
    editableInputType: 'string',
    sortable: true,
  },
  {
    key: 'comment',
    label: t('forecast.table.comment'),
    align: 'has-text-left',
    editable: () => true,
    editableInputType: 'string',
    sortable: true,
  },
  {
    key: 'actions',
  },
];

const BaseSubForecastColumns: (SubForecastColumnData | { key: '' })[] = [
  { key: 'name', align: 'has-text-left' },
  {
    key: 'budget',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'previousForecast',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'forecast',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'result',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'remaining',
    align: 'has-text-right',
    format: utils.formatNumber,
    class: getDangerStyleWhenNegative,
  },
  {
    key: 'budgetLeft',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'earnings',
    align: 'has-text-right',
    format: utils.formatNumber,
    class: getDangerStyleWhenNegative,
  },
  {
    key: '',
  },
  {
    key: 'comment',
    align: 'has-text-left',
    editable: () => true,
    editableInputType: 'string',
  },
  {
    key: '',
  },
];

const BaseForecastDetailColumns: (ForecastDetailColumnData | { key: '' })[] = [
  { key: 'month', align: 'has-text-left', format: utils.formatForecastDate },
  {
    key: 'budget',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'previousForecast',
    align: 'has-text-right',
    format: utils.formatNumber,
  },
  {
    key: 'forecast',
    align: 'has-text-right',
    format: utils.formatNumber,
    editable: () => true,
    editableInputType: 'number',
  },
  {
    key: 'result',
    align: 'has-text-right',
    format: utils.formatNumber,
    editable: editResultForAccount,
    editableInputType: 'number',
  },
  {
    key: 'remaining',
    align: 'has-text-right',
    format: (val) => (val == null ? 0 : utils.formatNumber(val)),
  },
  {
    key: '',
  },
  {
    key: '',
  },
  {
    key: '',
  },
  {
    key: 'comment',
    align: 'has-text-left',
    editable: () => true,
    editableInputType: 'string',
  },
  {
    key: 'actions',
    align: 'has-text-right',
  },
];

function createColumns<T extends { key: string | '' }>(
  baseColumns: T[],
  overrides: Partial<T>[] = [],
): T[] {
  return baseColumns.map((column) => {
    const override = overrides.find((o) => o.key === column.key);
    return { ...column, ...override };
  });
}

// Forecast Columns
export const ForecastColumns: ForecastColumnData[] =
  createColumns<ForecastColumnData>(BaseForecastColumns);
export const SubForecastColumns: SubForecastColumnData[] =
  createColumns<SubForecastColumnData>(BaseSubForecastColumns);
export const ForecastDetailColumns: ForecastDetailColumnData[] =
  createColumns<ForecastDetailColumnData>(BaseForecastDetailColumns);
export const ForecastDetailATAFixedColumns: ForecastDetailColumnData[] =
  createColumns<ForecastDetailColumnData>(BaseForecastDetailColumns, [
    { key: 'forecast', editable: () => false },
  ]);
export const ForecastDetailATAOngoingColumns: ForecastDetailColumnData[] =
  createColumns<ForecastDetailColumnData>(BaseForecastDetailColumns, [
    { key: 'forecast', editable: () => false },
  ]);

//ATA Fixed Columns
export const ATAFixedColumns: ForecastColumnData[] =
  createColumns<ForecastColumnData>(BaseForecastColumns);
export const SubATAFixedColumns: SubForecastColumnData[] =
  createColumns<SubForecastColumnData>(BaseSubForecastColumns);
export const DetailATAFixedColumns: ForecastDetailColumnData[] =
  createColumns<ForecastDetailColumnData>(BaseForecastDetailColumns, [
    { key: 'budget', editable: () => true, editableInputType: 'number' },
  ]);
export const DetailATAFixedIsNotForecastedColumns: ForecastDetailColumnData[] =
  createColumns<ForecastDetailColumnData>(BaseForecastDetailColumns, [
    { key: 'budget', editable: () => true, editableInputType: 'number' },
    { key: 'result', editable: () => true, editableInputType: 'number' },
  ]);

//ATA Ongoing Columns
export const ATAOngoingColumns: ForecastColumnData[] =
  createColumns<ForecastColumnData>(BaseForecastColumns);
export const SubATAOngoingColumns: SubForecastColumnData[] =
  createColumns<SubForecastColumnData>(BaseSubForecastColumns);
export const DetailATAOngoingColumns: ForecastDetailColumnData[] =
  createColumns<ForecastDetailColumnData>(BaseForecastDetailColumns);
